"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useIdleReady } from "@/shared/hooks/use-idle-ready";
import { OPEN_MODAL_DIALOG_SELECTOR, isOpenModalDialogPresent } from "@/shared/lib/dom/is-open-modal-dialog-present";

/** How long after the last keystroke the popup waits. */
export const TYPING_QUIET_MS = 2000;
const TYPING_TARGET = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const isTypingTarget = (element: Element | null): boolean => element !== null && element.matches(TYPING_TARGET);

/**
 * Whether the page is in a state a popup may land on: the browser has been
 * idle once, no dialog is open, and the broker is not typing (no focused field
 * and no keystroke in the last 2 s). A spotlight that appears over an open
 * dialog or under someone's cursor mid-sentence was never seen.
 */
export function useHostReadiness() {
  const $ = _c(4);
  const idle = useIdleReady();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typing, setTyping] = useState(_temp);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const check = () => setDialogOpen(isOpenModalDialogPresent(document));
      check();
      const observer = new MutationObserver(check);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-state", "role"]
      });
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      let timer;
      const settle = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => setTyping(isTypingTarget(document.activeElement)), TYPING_QUIET_MS);
      };
      const onKey = () => {
        setTyping(true);
        settle();
      };
      const onFocusIn = event => {
        if (isTypingTarget(event.target as Element | null)) {
          setTyping(true);
        }
      };
      const onFocusOut = () => settle();
      window.addEventListener("keydown", onKey, true);
      document.addEventListener("focusin", onFocusIn);
      document.addEventListener("focusout", onFocusOut);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("keydown", onKey, true);
        document.removeEventListener("focusin", onFocusIn);
        document.removeEventListener("focusout", onFocusOut);
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  return idle && !dialogOpen && !typing;
}
function _temp() {
  return typeof document !== "undefined" && isTypingTarget(document.activeElement);
}
export { OPEN_MODAL_DIALOG_SELECTOR };